import React from "react";
import { Link } from "react-router-dom";
import { useBranding } from "../lib/branding";

// Wording rules (CLAUDE.md): nothing here claims a figure is "verified" or a charge is improper,
// nothing is legal advice, and nothing tells anyone to withhold payment.
const STEPS = [
    ["Add the figures", "Type in the budget and what was spent, or upload the agent's statement."],
    ["See what each rests on", "Every line shows its source document, or says plainly that it has none."],
    ["See what is missing", "A 12-point check of the annual pack lists what is not in the records you can see."],
    ["Ask for it, on the record", "Turn the gaps into a draft request you review and send yourself. It is tracked until answered."],
];

const PROMISES = [
    "Every pound is traced to a document, or marked as having none.",
    "A figure nobody has checked is never presented as fact.",
    "No legal advice. Letters are drafts for you to review.",
    "It never suggests withholding or delaying a payment.",
    "Residents' personal circumstances are not shown to other residents.",
];

export default function Landing() {
    const brand = useBranding();
    return (
        <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
            <header className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
                <div className="font-heading text-xl font-bold" data-testid="landing-brand">{brand.product_name}</div>
                <nav className="flex items-center gap-4 text-sm">
                    <Link to="/login" className="underline" data-testid="landing-signin">Sign in</Link>
                    <Link to="/signup" className="rounded bg-slate-900 text-white px-3 py-1.5" data-testid="landing-signup">
                        Create a workspace
                    </Link>
                </nav>
            </header>

            <main className="max-w-5xl mx-auto px-6">
                <section className="py-14 max-w-3xl">
                    <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight" data-testid="landing-headline">
                        Where did your estate's money go?
                    </h1>
                    <p className="mt-4 text-lg text-slate-600">{brand.tagline}.</p>
                    <p className="mt-3 text-slate-600">
                        For the residents and directors of an estate, whether or not a managing agent runs it.
                        See what you are paying for, what the agent's pack is missing, and ask for it properly.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <Link to="/signup" className="rounded bg-slate-900 text-white px-5 py-2.5 text-sm">Get started</Link>
                        <Link to="/login" className="rounded border border-slate-300 px-5 py-2.5 text-sm">Sign in</Link>
                    </div>
                </section>

                <section className="py-8" aria-labelledby="how">
                    <h2 id="how" className="font-heading text-2xl font-semibold">How it works</h2>
                    <ol className="mt-5 grid gap-4 md:grid-cols-4">
                        {STEPS.map(([title, body], index) => (
                            <li key={title} className="rounded-lg border border-slate-200 p-4" data-testid={`landing-step-${index + 1}`}>
                                <div className="text-xs uppercase tracking-widest text-slate-400">Step {index + 1}</div>
                                <div className="mt-1 font-semibold">{title}</div>
                                <p className="mt-1 text-sm text-slate-600">{body}</p>
                            </li>
                        ))}
                    </ol>
                </section>

                <section className="py-8" aria-labelledby="promises">
                    <h2 id="promises" className="font-heading text-2xl font-semibold">What it will always do, and never do</h2>
                    <ul className="mt-4 space-y-2 text-slate-700" data-testid="landing-promises">
                        {PROMISES.map((text) => (
                            <li key={text} className="flex gap-2"><span aria-hidden="true">·</span>{text}</li>
                        ))}
                    </ul>
                </section>
            </main>

            <footer className="max-w-5xl mx-auto px-6 py-10 text-xs text-slate-500 flex gap-4">
                <Link to="/privacy-notice" className="underline">Privacy notice</Link>
                <Link to="/terms" className="underline">Terms</Link>
                <span>Not legal advice.</span>
            </footer>
        </div>
    );
}
